import { useMemo } from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Layers, Folder, MessageCircle, Tag, Plus, Trash2, Search, Loader2, FolderOpen } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import { topicsService } from "@/services/topicsService";
import { chatThreadsService } from "@/services/chatThreadsService";
import { useTags } from "@/hooks/useTags";
import { tagsService } from "@/services/tagsService";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { sourcesService } from "@/services/sourcesService";
import { foldersService, type LibraryFolder } from "@/services/foldersService";
import type { MastermindSpace, Topic } from "@shared/schema";

interface LibraryItem {
  id: string;
  title: string;
  itemType: "note" | "reference";
  folderId?: string | null;
  linkedSpaceIds: string[];
  linkedTopicIds: string[];
  linkedThreadIds: string[];
  tags: string[];
  originalItem?: any;
}

interface RelationshipInspectorDrawerProps {
  item: LibraryItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRelationshipsUpdated?: () => void;
}


export function RelationshipInspectorDrawer({
  item,
  open,
  onOpenChange,
  onRelationshipsUpdated,
}: RelationshipInspectorDrawerProps) {

  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("spaces");
  const [searchQuery, setSearchQuery] = useState("");
  const [mutatingIds, setMutatingIds] = useState<Set<string>>(new Set());

  const addMutatingId = (id: string) => setMutatingIds((prev) => new Set(prev).add(id));
  const removeMutatingId = (id: string) => setMutatingIds((prev) => {
    const next = new Set(prev);
    next.delete(id);
    return next;
  });

  const { data: allTagsRaw = [] } = useTags();

  // Normalize linkedThreadIds - handle case where it might be a JSON string
  const normalizedItem = useMemo(() => {
    if (!item) return null;
    
    let linkedThreadIds = item.linkedThreadIds;
    
    // If linkedThreadIds is a string, try to parse it as JSON
    if (typeof linkedThreadIds === 'string') {
      try {
        const parsed = JSON.parse(linkedThreadIds);
        linkedThreadIds = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        // If parsing fails, treat as empty array
        linkedThreadIds = [];
      }
    }
    
    // Ensure it's always an array
    if (!Array.isArray(linkedThreadIds)) {
      linkedThreadIds = [];
    }
    
    const tagsById = new Map(allTagsRaw.map((t) => [String(t.id), t.name]));
    const rawTags = Array.isArray(item.tags) ? item.tags : [];
    const displayTags =
      item.itemType === "note"
        ? rawTags
            .map((t) => {
              const s = String(t);
              return tagsById.get(s) ?? s;
            })
            .filter((name) => name.length > 0)
        : rawTags.map(String);

    return {
      ...item,
      linkedThreadIds,
      tags: displayTags,
    };
  }, [item, allTagsRaw]);

  const { data: spaces = [] } = useQuery<MastermindSpace[]>({
    queryKey: ["/api/mastermind-spaces", user?.id, currentWorkspaceId ?? null],
    queryFn: async () => {
      if (!user) return [];
      return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
    },
    enabled: !!user && open,
  });

  const { data: topics = [] } = useQuery<Topic[]>({
    queryKey: ["/api/topics/all", user?.id, currentWorkspaceId ?? null],
    queryFn: async () => {
      if (!user) return [];
      return await topicsService.getAllTopics(currentWorkspaceId ?? null);
    },
    enabled: !!user && open,
  });

  const { data: threads = [] } = useQuery({
    queryKey: ["/api/chat-threads", user?.id, currentWorkspaceId ?? null],
    queryFn: async () => {
      if (!user) return [];
      return await chatThreadsService.getThreadSummaries(currentWorkspaceId ?? null);
    },
    enabled: !!user && open,
  });

  const allTags = useMemo(
    () =>
      allTagsRaw.map((t) => ({
        id: t.id,
        name: t.name,
        color: t.color ?? undefined,
      })),
    [allTagsRaw]
  );

  const { data: folders = [] } = useQuery<LibraryFolder[]>({
    queryKey: ["/api/library-folders", user?.id, currentWorkspaceId ?? "personal"],
    queryFn: async () => {
      if (!user) return [];
      return await foldersService.getFolders(currentWorkspaceId ?? null);
    },
    enabled: !!user && open,
    staleTime: 60000, // Cache for 1 minute
  });

  const spacesMap = useMemo(() => new Map(spaces.map((s) => [s.id, s])), [spaces]);
  const topicsMap = useMemo(() => new Map(topics.map((t) => [t.id, t])), [topics]);
  const threadsMap = useMemo(() => new Map(threads.map((t) => [t.id, t])), [threads]);
  const foldersMap = useMemo(() => new Map(folders.map((f) => [f.id, f])), [folders]);
  
  // Build folder tree for display
  const folderTree = useMemo(() => {
    return foldersService.buildFolderTree(folders);
  }, [folders]);

  // Flatten all folders (including nested) for search/filtering
  const flattenFolders = useMemo(() => {
    const result: Array<LibraryFolder & { level: number }> = [];
    const traverse = (folderList: LibraryFolder[], level: number = 0) => {
      folderList.forEach(folder => {
        result.push({ ...folder, level });
        const children = foldersService.getChildFolders(folders, folder.id);
        if (children.length > 0) {
          traverse(children, level + 1);
        }
      });
    };
    traverse(folderTree);
    return result;
  }, [folderTree, folders]);

  const filteredSpaces = useMemo(() => {
    if (!searchQuery) return spaces;
    return spaces.filter((s) => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [spaces, searchQuery]);

  const filteredTopics = useMemo(() => {
    if (!searchQuery) return topics;
    return topics.filter((t) => t.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [topics, searchQuery]);

  const filteredThreads = useMemo(() => {
    if (!searchQuery) return threads;
    return threads.filter((t) => t.title?.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [threads, searchQuery]);

  const filteredTags = useMemo(() => {
    if (!searchQuery) return allTags;
    return allTags.filter((t) => t.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [allTags, searchQuery]);

  const filteredFolders = useMemo(() => {
    if (!searchQuery) return flattenFolders;
    return flattenFolders.filter((f) => f.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [flattenFolders, searchQuery]);

  const isSpaceLinked = (spaceId: string) => normalizedItem?.linkedSpaceIds.includes(spaceId) ?? false;
  const isTopicLinked = (topicId: string) => normalizedItem?.linkedTopicIds.includes(topicId) ?? false;
  const isThreadLinked = (threadId: string) => normalizedItem?.linkedThreadIds.includes(threadId) ?? false;
  const isTagLinked = (tagName: string) => normalizedItem?.tags.includes(tagName) ?? false;
  const isInFolder = (folderId: string | null) => normalizedItem?.folderId === folderId;

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base"] });
    queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary", user?.id] });
    queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
  };

  const handleMoveToFolder = async (folderId: string | null) => {
    if (!item) return;
    const folderName = folderId ? foldersMap.get(folderId)?.name || "folder" : "No folder";
    addMutatingId(`folder-${folderId || "none"}`);
    try {
      if (item.itemType === "note") {
        await knowledgeBaseService.updateKnowledgeBaseItem(item.id, {
          folderId: folderId,
        });
      } else {
        await sourcesService.updateSource(item.id, {
          folderId: folderId,
        } as any);
      }
      item.folderId = folderId;
      toast({ 
        title: "Folder updated", 
        description: `"${item.title}" moved to ${folderId ? folderName : "no folder"}` 
      });
      invalidateQueries();
      onRelationshipsUpdated?.();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to move to folder", variant: "destructive" });
    } finally {
      removeMutatingId(`folder-${folderId || "none"}`);
    }
  };

  const handleLinkSpace = async (spaceId: string) => {
    if (!item) return;
    addMutatingId(`space-${spaceId}`);
    try {
      if (item.itemType === "note") {
        await knowledgeBaseService.updateKnowledgeBaseItem(item.id, {
          spaceId: spaceId,
        });
      } else {
        const currentSpaceIds = Array.isArray(item.linkedSpaceIds) ? item.linkedSpaceIds : [];
        await sourcesService.updateSource(item.id, {
          linkedSpaceIds: [...currentSpaceIds, spaceId],
        } as any);
      }
      item.linkedSpaceIds = [...(item.linkedSpaceIds || []), spaceId];
      toast({ title: "Space linked", description: `Space linked to "${item.title}"` });
      invalidateQueries();
      onRelationshipsUpdated?.();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to link space", variant: "destructive" });
    } finally {
      removeMutatingId(`space-${spaceId}`);
    }
  };

  const handleUnlinkSpace = async (spaceId: string) => {
    if (!item) return;
    addMutatingId(`space-${spaceId}`);
    try {
      if (item.itemType === "note") {
        await knowledgeBaseService.updateKnowledgeBaseItem(item.id, {
          spaceId: null,
        });
      } else {
        const currentSpaceIds = Array.isArray(item.linkedSpaceIds) ? item.linkedSpaceIds : [];
        await sourcesService.updateSource(item.id, {
          linkedSpaceIds: currentSpaceIds.filter((id) => id !== spaceId),
        } as any);
      }
      item.linkedSpaceIds = (item.linkedSpaceIds || []).filter((id) => id !== spaceId);
      toast({ title: "Space unlinked", description: `Space unlinked from "${item.title}"` });
      invalidateQueries();
      onRelationshipsUpdated?.();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to unlink space", variant: "destructive" });
    } finally {
      removeMutatingId(`space-${spaceId}`);
    }
  };

  const handleLinkTopic = async (topicId: string) => {
    if (!item) return;
    addMutatingId(`topic-${topicId}`);
    try {
      if (item.itemType === "note") {
        await knowledgeBaseService.updateKnowledgeBaseItem(item.id, {
          topicId: topicId,
        });
      } else {
        const currentTopicIds = Array.isArray(item.linkedTopicIds) ? item.linkedTopicIds : [];
        await sourcesService.updateSource(item.id, {
          topics: [...currentTopicIds, topicId],
        });
      }
      item.linkedTopicIds = [...(item.linkedTopicIds || []), topicId];
      toast({ title: "Topic linked", description: `Topic linked to "${item.title}"` });
      invalidateQueries();
      onRelationshipsUpdated?.();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to link topic", variant: "destructive" });
    } finally {
      removeMutatingId(`topic-${topicId}`);
    }
  };

  const handleUnlinkTopic = async (topicId: string) => {
    if (!item) return;
    addMutatingId(`topic-${topicId}`);
    try {
      if (item.itemType === "note") {
        await knowledgeBaseService.updateKnowledgeBaseItem(item.id, {
          topicId: null,
        });
      } else {
        const currentTopicIds = Array.isArray(item.linkedTopicIds) ? item.linkedTopicIds : [];
        await sourcesService.updateSource(item.id, {
          topics: currentTopicIds.filter((id) => id !== topicId),
        });
      }
      item.linkedTopicIds = (item.linkedTopicIds || []).filter((id) => id !== topicId);
      toast({ title: "Topic unlinked", description: `Topic unlinked from "${item.title}"` });
      invalidateQueries();
      onRelationshipsUpdated?.();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to unlink topic", variant: "destructive" });
    } finally {
      removeMutatingId(`topic-${topicId}`);
    }
  };

  const handleLinkThread = async (threadId: string) => {
    if (!item || !normalizedItem) return;
    addMutatingId(`thread-${threadId}`);
    try {
      if (item.itemType === "reference") {
        const currentThreadIds = normalizedItem.linkedThreadIds;
        await sourcesService.updateSource(item.id, {
          linkedThreads: [...currentThreadIds, threadId],
        });
        item.linkedThreadIds = [...currentThreadIds, threadId];
        toast({ title: "Thread linked", description: `Thread linked to "${item.title}"` });
        invalidateQueries();
        onRelationshipsUpdated?.();
      } else {
        toast({ title: "Info", description: "Thread linking is only available for references" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to link thread", variant: "destructive" });
    } finally {
      removeMutatingId(`thread-${threadId}`);
    }
  };

  const handleUnlinkThread = async (threadId: string) => {
    if (!item || !normalizedItem) return;
    addMutatingId(`thread-${threadId}`);
    try {
      if (item.itemType === "reference") {
        const currentThreadIds = normalizedItem.linkedThreadIds;
        await sourcesService.updateSource(item.id, {
          linkedThreads: currentThreadIds.filter((id) => id !== threadId),
        });
        item.linkedThreadIds = currentThreadIds.filter((id) => id !== threadId);
        toast({ title: "Thread unlinked", description: `Thread unlinked from "${item.title}"` });
        invalidateQueries();
        onRelationshipsUpdated?.();
      } else {
        toast({ title: "Info", description: "Thread linking is only available for references" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to unlink thread", variant: "destructive" });
    } finally {
      removeMutatingId(`thread-${threadId}`);
    }
  };

  const handleLinkTag = async (tagName: string) => {
    if (!item) return;
    addMutatingId(`tag-${tagName}`);
    try {
      if (item.itemType === "note") {
        const tag = allTags.find((t) => t.name === tagName);
        if (tag) {
          const meta = item.originalItem?.metadata;
          let parsed: Record<string, unknown> = {};
          if (typeof meta === "string") {
            try {
              parsed = JSON.parse(meta) as Record<string, unknown>;
            } catch {
              parsed = {};
            }
          } else if (meta && typeof meta === "object") {
            parsed = meta as Record<string, unknown>;
          }
          const fromMeta = Array.isArray(parsed.tagIds) ? parsed.tagIds : [];
          const currentTagIds = (fromMeta.length ? fromMeta : item.originalItem?.tagIds ?? []).map(
            String
          );
          if (currentTagIds.includes(tag.id)) return;
          await knowledgeBaseService.updateKnowledgeBaseItem(item.id, {
            tagIds: [...currentTagIds, tag.id],
          });
        }
      } else {
        const currentTags = Array.isArray(item.tags) ? item.tags : [];
        await sourcesService.updateSource(item.id, {
          tags: [...currentTags, tagName],
        });
      }
      item.tags = [...(item.tags || []), tagName];
      toast({ title: "Tag added", description: `Tag "${tagName}" added to "${item.title}"` });
      invalidateQueries();
      onRelationshipsUpdated?.();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to add tag", variant: "destructive" });
    } finally {
      removeMutatingId(`tag-${tagName}`);
    }
  };

  const handleUnlinkTag = async (tagName: string) => {
    if (!item) return;
    addMutatingId(`tag-${tagName}`);
    try {
      if (item.itemType === "note") {
        const tag = allTags.find((t) => t.name === tagName);
        if (tag) {
          const meta = item.originalItem?.metadata;
          let parsed: Record<string, unknown> = {};
          if (typeof meta === "string") {
            try {
              parsed = JSON.parse(meta) as Record<string, unknown>;
            } catch {
              parsed = {};
            }
          } else if (meta && typeof meta === "object") {
            parsed = meta as Record<string, unknown>;
          }
          const fromMeta = Array.isArray(parsed.tagIds) ? parsed.tagIds : [];
          const currentTagIds = (fromMeta.length ? fromMeta : item.originalItem?.tagIds ?? []).map(
            String
          );
          await knowledgeBaseService.updateKnowledgeBaseItem(item.id, {
            tagIds: currentTagIds.filter((id) => id !== tag.id),
          });
        }
      } else {
        const currentTags = Array.isArray(item.tags) ? item.tags : [];
        await sourcesService.updateSource(item.id, {
          tags: currentTags.filter((t) => t !== tagName),
        });
      }
      item.tags = (item.tags || []).filter((t) => t !== tagName);
      toast({ title: "Tag removed", description: `Tag "${tagName}" removed from "${item.title}"` });
      invalidateQueries();
      onRelationshipsUpdated?.();
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to remove tag", variant: "destructive" });
    } finally {
      removeMutatingId(`tag-${tagName}`);
    }
  };

  if (!item || !normalizedItem) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] sm:max-w-[400px] p-0 flex flex-col">
        <SheetHeader className="p-4 border-b">
          <SheetTitle className="text-lg font-semibold pr-8">
            Manage Relationships
          </SheetTitle>
          <p className="text-sm text-muted-foreground truncate">
            {normalizedItem.title}
          </p>
        </SheetHeader>

        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <TabsList className="grid grid-cols-5 mx-4 shrink-0">
            <TabsTrigger value="folder" className="text-xs gap-1">
              <FolderOpen className="h-3 w-3" />
              <span className="hidden sm:inline">Folder</span>
            </TabsTrigger>
            <TabsTrigger value="spaces" className="text-xs gap-1">
              <Layers className="h-3 w-3" />
              <span className="hidden sm:inline">Spaces</span>
              <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                {normalizedItem.linkedSpaceIds.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="topics" className="text-xs gap-1">
              <Folder className="h-3 w-3" />
              <span className="hidden sm:inline">Topics</span>
              <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                {normalizedItem.linkedTopicIds.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="threads" className="text-xs gap-1">
              <MessageCircle className="h-3 w-3" />
              <span className="hidden sm:inline">Threads</span>
              <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                {normalizedItem.linkedThreadIds.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="tags" className="text-xs gap-1">
              <Tag className="h-3 w-3" />
              <span className="hidden sm:inline">Tags</span>
              <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                {normalizedItem.tags.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 min-h-0">
            <TabsContent value="folder" className="mt-0 p-4">
              <div className="space-y-2">
                {normalizedItem.folderId && (
                  <>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Current Folder
                    </h4>
                    <div className="flex items-center justify-between p-2 rounded-md bg-amber-50 border border-amber-200 mb-4">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 text-amber-600" />
                        <span className="text-sm">{foldersMap.get(normalizedItem.folderId)?.name || "Unknown"}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={mutatingIds.has(`folder-none`)}
                        onClick={() => handleMoveToFolder(null)}
                      >
                        {mutatingIds.has(`folder-none`) ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <X className="h-3 w-3 text-destructive" />
                        )}
                      </Button>
                    </div>
                    <Separator className="my-4" />
                  </>
                )}
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  {normalizedItem.folderId ? "Move to Another Folder" : "Select Folder"}
                </h4>
                <div
                  className={`flex items-center justify-between p-2 rounded-md border hover:bg-muted/50 mb-2 ${mutatingIds.has(`folder-none`) ? "opacity-50 pointer-events-none" : "cursor-pointer"} ${!normalizedItem.folderId ? "bg-amber-50 border-amber-200" : ""}`}
                  onClick={() => !mutatingIds.has(`folder-none`) && !normalizedItem.folderId && handleMoveToFolder(null)}
                >
                  <div className="flex items-center gap-2">
                    <Folder className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">No folder (root)</span>
                  </div>
                  {mutatingIds.has(`folder-none`) ? (
                    <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                  ) : !normalizedItem.folderId ? (
                    <Badge variant="secondary" className="text-xs">Current</Badge>
                  ) : (
                    <Plus className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                {filteredFolders.map((folder) => {
                  const folderLevel = folder.level || 0;
                  const isCurrentFolder = isInFolder(folder.id);
                  return (
                    <div
                      key={folder.id}
                      className={`flex items-center justify-between p-2 rounded-md border hover:bg-muted/50 ${mutatingIds.has(`folder-${folder.id}`) ? "opacity-50 pointer-events-none" : isCurrentFolder ? "" : "cursor-pointer"} ${isCurrentFolder ? "bg-amber-50 border-amber-200" : ""}`}
                      onClick={() => !mutatingIds.has(`folder-${folder.id}`) && !isCurrentFolder && handleMoveToFolder(folder.id)}
                      style={{ paddingLeft: `${12 + folderLevel * 16}px` }}
                    >
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 text-amber-600" />
                        <span className="text-sm">{folder.name}</span>
                      </div>
                      {mutatingIds.has(`folder-${folder.id}`) ? (
                        <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                      ) : isCurrentFolder ? (
                        <Badge variant="secondary" className="text-xs">Current</Badge>
                      ) : (
                        <Plus className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  );
                })}
                {filteredFolders.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {searchQuery ? "No matching folders" : "No folders created yet"}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="spaces" className="mt-0 p-4">
              <div className="space-y-2">
                {normalizedItem.linkedSpaceIds.length > 0 && (
                  <>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Linked Spaces
                    </h4>
                    {normalizedItem.linkedSpaceIds.map((spaceId) => {
                      const space = spacesMap.get(spaceId);
                      return (
                        <div
                          key={spaceId}
                          className="flex items-center justify-between p-2 rounded-md bg-blue-50 border border-blue-200"
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: space?.color || "#3b82f6" }}
                            />
                            <span className="text-sm">{space?.name || "Unknown"}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            disabled={mutatingIds.has(`space-${spaceId}`)}
                            onClick={() => handleUnlinkSpace(spaceId)}
                          >
                            {mutatingIds.has(`space-${spaceId}`) ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3 text-destructive" />
                            )}
                          </Button>
                        </div>
                      );
                    })}
                    <Separator className="my-4" />
                  </>
                )}
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Available Spaces
                </h4>
                {filteredSpaces.filter((s) => !isSpaceLinked(s.id)).map((space) => (
                  <div
                    key={space.id}
                    className={`flex items-center justify-between p-2 rounded-md border hover:bg-muted/50 ${mutatingIds.has(`space-${space.id}`) ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}
                    onClick={() => !mutatingIds.has(`space-${space.id}`) && handleLinkSpace(space.id)}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: space.color || "#888" }}
                      />
                      <span className="text-sm">{space.name}</span>
                    </div>
                    {mutatingIds.has(`space-${space.id}`) ? (
                      <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                ))}
                {filteredSpaces.filter((s) => !isSpaceLinked(s.id)).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {searchQuery ? "No matching spaces" : "All spaces are linked"}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="topics" className="mt-0 p-4">
              <div className="space-y-2">
                {normalizedItem.linkedTopicIds.length > 0 && (
                  <>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Linked Topics
                    </h4>
                    {normalizedItem.linkedTopicIds.map((topicId) => {
                      const topic = topicsMap.get(topicId);
                      return (
                        <div
                          key={topicId}
                          className="flex items-center justify-between p-2 rounded-md bg-purple-50 border border-purple-200"
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: topic?.color || "#8b5cf6" }}
                            />
                            <span className="text-sm">{topic?.name || "Unknown"}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            disabled={mutatingIds.has(`topic-${topicId}`)}
                            onClick={() => handleUnlinkTopic(topicId)}
                          >
                            {mutatingIds.has(`topic-${topicId}`) ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3 text-destructive" />
                            )}
                          </Button>
                        </div>
                      );
                    })}
                    <Separator className="my-4" />
                  </>
                )}
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Available Topics
                </h4>
                {filteredTopics.filter((t) => !isTopicLinked(t.id)).map((topic) => (
                  <div
                    key={topic.id}
                    className={`flex items-center justify-between p-2 rounded-md border hover:bg-muted/50 ${mutatingIds.has(`topic-${topic.id}`) ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}
                    onClick={() => !mutatingIds.has(`topic-${topic.id}`) && handleLinkTopic(topic.id)}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: topic.color || "#888" }}
                      />
                      <span className="text-sm">{topic.name}</span>
                    </div>
                    {mutatingIds.has(`topic-${topic.id}`) ? (
                      <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                ))}
                {filteredTopics.filter((t) => !isTopicLinked(t.id)).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {searchQuery ? "No matching topics" : "All topics are linked"}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="threads" className="mt-0 p-4">
              <div className="space-y-2">
                {normalizedItem.linkedThreadIds.length > 0 && (
                  <>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Linked Threads
                    </h4>
                    {normalizedItem.linkedThreadIds.map((threadId) => {
                      const thread = threadsMap.get(threadId);
                      return (
                        <div
                          key={threadId}
                          className="flex items-center justify-between p-2 rounded-md bg-green-50 border border-green-200"
                        >
                          <div className="flex items-center gap-2">
                            <MessageCircle className="h-3 w-3 text-green-600" />
                            <span className="text-sm truncate max-w-[200px]">
                              {thread?.title || "Untitled Thread"}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            disabled={mutatingIds.has(`thread-${threadId}`)}
                            onClick={() => handleUnlinkThread(threadId)}
                          >
                            {mutatingIds.has(`thread-${threadId}`) ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3 text-destructive" />
                            )}
                          </Button>
                        </div>
                      );
                    })}
                    <Separator className="my-4" />
                  </>
                )}
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Available Threads
                </h4>
                {filteredThreads.filter((t) => !isThreadLinked(t.id)).slice(0, 20).map((thread) => (
                  <div
                    key={thread.id}
                    className={`flex items-center justify-between p-2 rounded-md border hover:bg-muted/50 ${mutatingIds.has(`thread-${thread.id}`) ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}
                    onClick={() => !mutatingIds.has(`thread-${thread.id}`) && handleLinkThread(thread.id)}
                  >
                    <div className="flex items-center gap-2">
                      <MessageCircle className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm truncate max-w-[200px]">
                        {thread.title || "Untitled Thread"}
                      </span>
                    </div>
                    {mutatingIds.has(`thread-${thread.id}`) ? (
                      <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                ))}
                {filteredThreads.filter((t) => !isThreadLinked(t.id)).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {searchQuery ? "No matching threads" : "All threads are linked"}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="tags" className="mt-0 p-4">
              <div className="space-y-2">
                {normalizedItem.tags.length > 0 && (
                  <>
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Current Tags
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {normalizedItem.tags.map((tagName) => (
                        <Badge
                          key={tagName}
                          variant="secondary"
                          className={`gap-1 bg-amber-50 border-amber-200 text-amber-800 ${mutatingIds.has(`tag-${tagName}`) ? "opacity-50" : "cursor-pointer hover:bg-amber-100"}`}
                          onClick={() => !mutatingIds.has(`tag-${tagName}`) && handleUnlinkTag(tagName)}
                        >
                          {mutatingIds.has(`tag-${tagName}`) ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Tag className="h-3 w-3" />
                          )}
                          {tagName}
                          {!mutatingIds.has(`tag-${tagName}`) && <X className="h-3 w-3 ml-1" />}
                        </Badge>
                      ))}
                    </div>
                    <Separator className="my-4" />
                  </>
                )}
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Available Tags
                </h4>
                <div className="flex flex-wrap gap-2">
                  {filteredTags.filter((t) => !isTagLinked(t.name)).map((tag) => (
                    <Badge
                      key={tag.id}
                      variant="outline"
                      className={`gap-1 ${mutatingIds.has(`tag-${tag.name}`) ? "opacity-50" : "cursor-pointer hover:bg-muted"}`}
                      onClick={() => !mutatingIds.has(`tag-${tag.name}`) && handleLinkTag(tag.name)}
                    >
                      {mutatingIds.has(`tag-${tag.name}`) ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Plus className="h-3 w-3" />
                      )}
                      {tag.name}
                    </Badge>
                  ))}
                </div>
                {filteredTags.filter((t) => !isTagLinked(t.name)).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {searchQuery ? "No matching tags" : "All tags are applied"}
                  </p>
                )}
              </div>
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
